/**
 * Phase 86 Plan 86-04 — MyTasksPage rewritten as the "Compost" design's
 * two-column body: top bar, 196px left rail, generated header sentence and
 * zone-banded table (`.planning/design/mytasks-compost/README.md`).
 * Supersedes the Phase 45/50 grouped-list + KPI band + horizontal filter-bar
 * layout — see `86-04-SUMMARY.md` for what changed and why.
 *
 * Row click opens the ticket in a slide-over over this list (Petra UAT
 * 2026-08-18); `MyTaskRow` only navigates when no host handler is supplied
 * (86-03, MKS-144) — this page no longer supplies an `onRowClick` handler
 * anywhere in the render tree; the old board deep-link wiring is retired.
 *
 * Query collapse (86-04 Task 3): the page now calls `useMyTasks` exactly
 * ONCE (no `tiers` option — the hook's query key would otherwise make a
 * second, distinct cache entry / network request per the tier selection).
 * Tier filtering is applied client-side, alongside the customer/project/
 * search filters, from that single result.
 *
 * Phase 50 Plan 50-06 — tier filter chip (FC-01):
 * - tierFilter state persists via sessionStorage `mytasks.tierFilter` + URL ?tiers=<csv>
 * - URL is source of truth on initial mount; sessionStorage is fallback
 * - availableTiers derived from the unfiltered task list (no N+1)
 *
 * Phase 86 Plan 86-04 — sort mode (`rot | pressure | due | project`)
 * persists via sessionStorage `mytasks.sortMode`, matching the tier filter's
 * pattern. T-86-13 (threat model): a persisted value is browser-writable,
 * so it is validated against the four known modes on read and falls back to
 * `rot` on anything else — never reaches a sort comparator lookup.
 *
 * Phase 86 Plan 86-06 — the keyboard + responsive layer. The page:
 *   - calls `useMyTasksViewportMode()` (owned by `MyTaskRow.tsx`, the single
 *     file that already owns the one locked grid template) and forwards the
 *     result to `MyTasksZonedList` and to its own rail-stacking layout
 *     decision, so the page shell, the header/rows and the rail can never
 *     disagree about which responsive mode is active.
 *   - calls `useMyTasksKeyboard`, handing it `visibleTaskOrder` (the exact
 *     render order of whichever sort mode is active — rot's zone groups,
 *     project's project groups, or the flat sorted list), and holds onto
 *     the returned `cursorTaskId` — this IS "the page owns the cursor
 *     state" per the plan: the hook's `useState` lives inside this
 *     component's call to it.
 *   - forwards `cursorTaskId` down to `MyTasksZonedList`, which marks the
 *     matching row; wires `⌘K` to a ref on `MyTasksTopBar`'s search input;
 *     and reaches the cursor row's own TOUCH state for the `T` shortcut via
 *     `MyTasksZonedList`'s `onReady` API rather than a parallel mutation
 *     path (carried-forward constraint from 86-05).
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMyTasks } from '../hooks/useMyTasks';
import { useSidebarCustomers } from '../hooks/useSidebarCustomers';
import { useMyTasksKeyboard } from '../hooks/useMyTasksKeyboard';
import { useAuthOptional } from '../services/authContext';
import { MyTasksTopBar } from '../components/mytasks/MyTasksTopBar';
import { MyTasksRail } from '../components/mytasks/MyTasksRail';
import { MyTasksHeader } from '../components/mytasks/MyTasksHeader';
import { MyTasksZonedList, type MyTasksZonedListApi } from '../components/mytasks/MyTasksZonedList';
import { MyTasksFreeToGrab } from '../components/mytasks/MyTasksFreeToGrab';
import { MtBulkCloseDialog } from '../components/mytasks/MtBulkCloseDialog';
import { PickSomethingUp } from '../components/mytasks/PickSomethingUp';
import { MyTasksTaskPanel } from '../components/mytasks/MyTasksTaskPanel';
import { useMyTasksViewportMode } from '../components/mytasks/MyTaskRow';
import type { FilterCustomer } from '../components/mytasks/filterTypes';
import type { ProjectGroupData } from '../components/mytasks/MyTasksProjectGroup';
import type { MyTaskDto } from '../services/api/tasks';
import type { SidebarCustomer } from '../services/api/sidebar';
import { taskMatchesSearch } from '../utils/taskSearch';
import { taskRotZone } from '../utils/format';
import {
  sortTasksByMode,
  groupTasksByZone,
  compostCounts as computeCompostCounts,
  generateHeaderSentence,
  type CompostSortMode,
} from '../utils/myTasksCompost';

const TIER_STORAGE_KEY = 'mytasks.tierFilter';
const SORT_MODE_STORAGE_KEY = 'mytasks.sortMode';
const VALID_SORT_MODES: readonly CompostSortMode[] = ['rot', 'pressure', 'due', 'project'];

const SUB_LINE_BY_MODE: Record<CompostSortMode, string> = {
  rot: 'sorted by rot · oldest activity at the bottom, where the flies are',
  pressure: 'sorted by pressure · SLA reaction, then due date',
  due: 'sorted by due date · earliest first',
  project: 'sorted by project · grouped alphabetically',
};

function parseTierIds(csv: string): number[] {
  return csv
    .split(',')
    .map(Number)
    .filter((n) => Number.isFinite(n) && n > 0);
}

/** T-86-13 mitigation — never let a tampered/garbage stored value reach a
 * sort comparator lookup; anything outside the four known modes falls back
 * to 'rot'. */
function readInitialSortMode(): CompostSortMode {
  try {
    const stored = sessionStorage.getItem(SORT_MODE_STORAGE_KEY);
    if (stored && (VALID_SORT_MODES as readonly string[]).includes(stored)) {
      return stored as CompostSortMode;
    }
  } catch {
    // sessionStorage unavailable (private browsing edge) — fall through.
  }
  return 'rot';
}

function groupByProject(tasks: MyTaskDto[]): ProjectGroupData[] {
  const map = new Map<number, ProjectGroupData>();
  for (const t of tasks) {
    const existing = map.get(t.projectId);
    if (existing) {
      existing.tasks.push(t);
    } else {
      map.set(t.projectId, {
        projectId: t.projectId,
        projectName: t.projectName,
        customerColorHex: t.customerColorHex ?? null,
        isSla: t.projectKind === 'sla',
        tasks: [t],
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => a.projectName.localeCompare(b.projectName));
}

// Quick task 260812-jj1 — wires the search box (previously dead state) into
// the task list. Matching is case- and diacritic-insensitive over task name
// and task code, via the shared board search helper (frontend/src/utils/
// taskSearch.ts). An empty or whitespace-only query matches every task.
export function filterTasks(
  tasks: MyTaskDto[],
  customerFilter: number | null,
  projectFilter: number | null,
  search: string,
): MyTaskDto[] {
  return tasks.filter((t) => {
    if (customerFilter !== null && t.customerId !== customerFilter) return false;
    if (projectFilter !== null && t.projectId !== projectFilter) return false;
    if (!taskMatchesSearch({ code: t.taskCode, name: t.taskName, description: null }, search)) {
      return false;
    }
    return true;
  });
}

function displayName(person: { firstName: string | null; lastName: string | null; email: string } | null): string {
  if (!person) return '';
  if (person.firstName && person.lastName) return `${person.firstName} ${person.lastName}`;
  return person.email;
}

export default function MyTasksPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const auth = useAuthOptional();
  const userDisplayName = displayName(auth?.impersonatedUser ?? auth?.user ?? null);
  // 260915-i0x — only the real user carries an avatar slot; while
  // impersonating, the displayed name is the target's, so fall back to the
  // name hash rather than colouring them as the admin.
  const userColorSeq = auth?.impersonatedUser ? null : (auth?.user?.avatarColorSeq ?? null);
  // MKS-203 — same impersonation-nulling reasoning as userColorSeq above.
  const userAvatarUrl = auth?.impersonatedUser ? null : (auth?.user?.avatarUrl ?? null);

  // Phase 86-06 — keyboard + responsive layer plumbing.
  const viewportMode = useMyTasksViewportMode();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const zonedListApiRef = useRef<MyTasksZonedListApi | null>(null);
  const handleZonedListReady = useCallback((api: MyTasksZonedListApi) => {
    zonedListApiRef.current = api;
  }, []);

  const [search, setSearch] = useState<string>('');
  const [customerFilter, setCustomerFilter] = useState<number | null>(null);
  const [projectFilter, setProjectFilter] = useState<number | null>(null);
  const [showDone, setShowDone] = useState<boolean>(false);
  const [grabOpen, setGrabOpen] = useState<boolean>(false);

  // Plan 50-06 (FC-01) — tier filter state: URL is source of truth on mount,
  // sessionStorage is fallback. One-shot initializer (deps array intentionally
  // empty to run once on mount only).
  const initialTiers = useMemo<number[]>(() => {
    const urlValue = searchParams.get('tiers') ?? '';
    if (urlValue) return parseTierIds(urlValue);
    const stored = sessionStorage.getItem(TIER_STORAGE_KEY) ?? '';
    if (stored) return parseTierIds(stored);
    return [];
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [tierFilter, setTierFilterState] = useState<number[]>(initialTiers);

  const setTierFilter = useCallback(
    (next: number[]) => {
      setTierFilterState(next);
      if (next.length) sessionStorage.setItem(TIER_STORAGE_KEY, next.join(','));
      else sessionStorage.removeItem(TIER_STORAGE_KEY);
      const params = new URLSearchParams(searchParams);
      if (next.length) params.set('tiers', next.join(','));
      else params.delete('tiers');
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const [sortMode, setSortModeState] = useState<CompostSortMode>(readInitialSortMode);
  const setSortMode = useCallback((mode: CompostSortMode) => {
    setSortModeState(mode);
    try {
      sessionStorage.setItem(SORT_MODE_STORAGE_KEY, mode);
    } catch {
      // sessionStorage unavailable — the in-memory state still updates.
    }
  }, []);

  // ONE query instance (86-04 Task 3 — see file header). Tier filtering now
  // happens client-side below, not via the hook's `tiers` option.
  const { data, isLoading, isError, refetch } = useMyTasks({ includeDone: showDone });
  const { data: customers } = useSidebarCustomers();

  const allTasksUnfiltered = useMemo(() => data ?? [], [data]);
  const sidebarCustomers: SidebarCustomer[] = useMemo(() => customers ?? [], [customers]);

  // Available tiers: derived from the UNFILTERED task list so options don't
  // disappear when a tier is already selected.
  const availableTiers = useMemo(() => {
    const map = new Map<number, { id: number; orderIndex: number; name: string }>();
    for (const t of allTasksUnfiltered) {
      if (t.tier) map.set(t.tier.id, t.tier);
    }
    return Array.from(map.values()).sort((a, b) => a.orderIndex - b.orderIndex);
  }, [allTasksUnfiltered]);

  const tierFiltered = useMemo(
    () =>
      tierFilter.length
        ? allTasksUnfiltered.filter((t) => t.tier != null && tierFilter.includes(t.tier.id))
        : allTasksUnfiltered,
    [allTasksUnfiltered, tierFilter],
  );

  // Build the filter source from the user's actual assigned tasks (post tier
  // filter) so every project / client that shows in the list is also
  // filterable — even when the user is not a sidebar member of that
  // customer (e.g. SLA Hour Package projects where tasks land via direct
  // assignee_id without a ProjectAssignment row).
  const filterCustomers: FilterCustomer[] = useMemo(() => {
    const sidebarNameById = new Map<number, string>();
    for (const c of sidebarCustomers) sidebarNameById.set(c.id, c.name);

    const byCustomer = new Map<number | null, { name: string; projects: Map<number, string> }>();
    for (const t of tierFiltered) {
      const cid = t.customerId ?? null;
      const existing = byCustomer.get(cid);
      const projects = existing ? existing.projects : new Map<number, string>();
      projects.set(t.projectId, t.projectName);
      if (!existing) {
        // Petra UAT 2026-08-18 — the row's own customerName wins. It comes
        // from the API (Plan 86-02) and is authoritative; the sidebar only
        // lists customers the user is a member of, so relying on it alone
        // showed "Customer #171" for a customer that has a perfectly good
        // name. The id fallback now fires only when nobody knows the name —
        // e.g. a project pointing at a customer row that no longer exists.
        const name =
          cid === null
            ? 'No customer'
            : t.customerName ?? sidebarNameById.get(cid) ?? `Customer #${cid}`;
        byCustomer.set(cid, { name, projects });
      }
    }

    return Array.from(byCustomer.entries())
      .map(([cid, v]) => ({
        id: cid ?? -1,
        name: v.name,
        projects: Array.from(v.projects.entries())
          .map(([pid, pname]) => ({ id: pid, name: pname }))
          .sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [tierFiltered, sidebarCustomers]);

  const tasks = useMemo(
    () => filterTasks(tierFiltered, customerFilter, projectFilter, search),
    [tierFiltered, customerFilter, projectFilter, search],
  );

  const sortedTasks = useMemo(() => sortTasksByMode(tasks, sortMode), [tasks, sortMode]);
  const zoneGroups = useMemo(() => groupTasksByZone(tasks), [tasks]);
  const projectGroups = useMemo(() => groupByProject(tasks), [tasks]);
  const counts = useMemo(() => computeCompostCounts(tasks), [tasks]);
  const sentence = useMemo(() => generateHeaderSentence(counts, tasks.length), [counts, tasks.length]);

  // Phase 86-06 — id → task lookup for the keyboard layer's open/touch
  // handlers, which only ever receive a bare taskId from the hook.
  const taskById = useMemo(() => {
    const map = new Map<number, MyTaskDto>();
    for (const t of tasks) map.set(t.taskId, t);
    return map;
  }, [tasks]);

  // Phase 86-06 — the EXACT render order `MyTasksZonedList` produces for the
  // active sort mode, so the keyboard cursor moves through the same rows a
  // sighted user sees, in the same order, skipping zone bands entirely
  // (they were never part of any of these three lists to begin with).
  const visibleTaskOrder = useMemo<number[]>(() => {
    if (sortMode === 'rot') return zoneGroups.flatMap((g) => g.tasks.map((t) => t.taskId));
    if (sortMode === 'project') return projectGroups.flatMap((g) => g.tasks.map((t) => t.taskId));
    return sortedTasks.map((t) => t.taskId);
  }, [sortMode, zoneGroups, projectGroups, sortedTasks]);

  // Petra UAT 2026-08-18 — the ticket opens OVER this list, not instead of it.
  // Navigating to /tasks/:ref lost the list mid-triage, and that page's close
  // button lands on the project board, so one glance ended two screens away.
  const [openTaskRef, setOpenTaskRef] = useState<string | null>(null);

  const handleOpenTask = useCallback(
    (taskId: number) => {
      const t = taskById.get(taskId);
      if (!t) return;
      setOpenTaskRef(t.taskCode ?? String(t.taskId));
    },
    [taskById],
  );

  const handleCloseTask = useCallback(() => setOpenTaskRef(null), []);

  const handleTouchTaskId = useCallback((taskId: number) => {
    zonedListApiRef.current?.triggerTouch(taskId);
  }, []);

  // `T` is only eligible on a stale-zone row — that is the only zone whose
  // chip IS the TOUCH action (graveyard's chip is REVIVE, which offers its
  // own "Comment" menu item instead).
  const canTouchTaskId = useCallback(
    (taskId: number) => {
      const t = taskById.get(taskId);
      if (!t) return false;
      return taskRotZone(t.lastActivityAt, { isTerminal: t.isTerminal }) === 'stale';
    },
    [taskById],
  );

  const { cursorTaskId } = useMyTasksKeyboard({
    taskIds: visibleTaskOrder,
    onOpen: handleOpenTask,
    onTouch: handleTouchTaskId,
    canTouch: canTouchTaskId,
    searchInputRef,
    enabled: !isLoading && !isError,
  });

  // Plan 86-05 Task 3 — BULK CLOSE targets: the graveyard zone's own tasks
  // (86-04's slot contract), reduced to just what useCloseTasks needs.
  const graveyardCloseTargets = useMemo(
    () =>
      (zoneGroups.find((g) => g.zone === 'graveyard')?.tasks ?? []).map((t) => ({
        taskId: t.taskId,
        projectId: t.projectId,
        projectName: t.projectName,
      })),
    [zoneGroups],
  );

  const sortCounts: Record<CompostSortMode, number> = {
    rot: tasks.length,
    pressure: tasks.length,
    due: tasks.length,
    project: projectGroups.length,
  };

  const hasActiveFilter =
    customerFilter !== null ||
    projectFilter !== null ||
    showDone ||
    tierFilter.length > 0 ||
    search.trim() !== '';

  const filterHint = hasActiveFilter
    ? `Showing ${tasks.length} of ${allTasksUnfiltered.length} tasks across ${projectGroups.length} project${projectGroups.length === 1 ? '' : 's'}`
    : null;

  const onClearFilters = useCallback(() => {
    setCustomerFilter(null);
    setProjectFilter(null);
    setShowDone(false);
    setTierFilter([]);
    setSearch('');
  }, [setTierFilter]);

  const openFreeToGrab = useCallback(() => setGrabOpen(true), []);

  // Phase 86-06 — below the card breakpoint the rail stacks above the table
  // instead of sitting beside it (README "Responsive" + this plan's own
  // "collapsing it above the list is the least surprising option").
  const bodyLayoutClass =
    viewportMode === 'card' ? 'flex-1 flex flex-col overflow-hidden' : 'flex-1 flex overflow-hidden';
  const railStacked = viewportMode === 'card';

  if (isLoading) {
    return (
      <div className="bg-ms-canvas flex-1 flex flex-col overflow-hidden">
        <MyTasksTopBar
          ref={searchInputRef}
          search={search}
          onSearchChange={setSearch}
          userDisplayName={userDisplayName}
          userColorSeq={userColorSeq}
          userAvatarUrl={userAvatarUrl}
        />
        <div className={bodyLayoutClass}>
          <MyTasksRail
            sortMode={sortMode}
            onSortModeChange={setSortMode}
            sortCounts={{ rot: 0, pressure: 0, due: 0, project: 0 }}
            compostCounts={{ staleCount: 0, graveyardCount: 0 }}
            customers={[]}
            customerFilter={customerFilter}
            onCustomerFilterChange={setCustomerFilter}
            projectFilter={projectFilter}
            onProjectFilterChange={setProjectFilter}
            availableTiers={[]}
            tierFilter={tierFilter}
            onTierFilterChange={setTierFilter}
            showDone={showDone}
            onShowDoneChange={setShowDone}
            stacked={railStacked}
          />
          <div className="flex-1 flex flex-col overflow-hidden">
            <MyTasksHeader sentence="Loading your tasks…" subLine="" />
            <div className="flex-1 overflow-y-auto" data-testid="my-tasks-skeleton">
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="h-[44px] bg-ms-surface-2/40 animate-pulse border-b border-ms-border-subtle"
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-ms-canvas flex-1 flex flex-col overflow-hidden">
        <MyTasksTopBar
          ref={searchInputRef}
          search={search}
          onSearchChange={setSearch}
          userDisplayName={userDisplayName}
          userColorSeq={userColorSeq}
          userAvatarUrl={userAvatarUrl}
        />
        <div className={bodyLayoutClass}>
          <MyTasksRail
            sortMode={sortMode}
            onSortModeChange={setSortMode}
            sortCounts={{ rot: 0, pressure: 0, due: 0, project: 0 }}
            compostCounts={{ staleCount: 0, graveyardCount: 0 }}
            customers={[]}
            customerFilter={customerFilter}
            onCustomerFilterChange={setCustomerFilter}
            projectFilter={projectFilter}
            onProjectFilterChange={setProjectFilter}
            availableTiers={[]}
            tierFilter={tierFilter}
            onTierFilterChange={setTierFilter}
            showDone={showDone}
            onShowDoneChange={setShowDone}
            stacked={railStacked}
          />
          <div className="flex-1 flex items-center justify-center p-8 gap-3 text-[12px] text-rose-600">
            Couldn&apos;t load your tasks. Try again.
            <button
              type="button"
              onClick={() => refetch()}
              className="px-3 py-1.5 text-[11.5px] font-medium border border-ms-orange/40 bg-ms-orange/5 text-ms-orange-dark rounded-md hover:bg-ms-orange/10"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Empty-states spec (README): zero tasks assigned → the FREE TO GRAB band
  // is the only content, no rail, no header sentence, no table.
  if (allTasksUnfiltered.length === 0) {
    return (
      <div className="bg-ms-canvas flex-1 flex flex-col overflow-hidden" data-testid="my-tasks-zero-state">
        <MyTasksTopBar
          ref={searchInputRef}
          search={search}
          onSearchChange={setSearch}
          userDisplayName={userDisplayName}
          userColorSeq={userColorSeq}
          userAvatarUrl={userAvatarUrl}
        />
        <div className="flex-1 flex flex-col overflow-y-auto">
          <MyTasksFreeToGrab onOpen={openFreeToGrab} />
          {grabOpen && <PickSomethingUp />}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-ms-canvas flex-1 flex flex-col overflow-hidden">
      <MyTasksTopBar
        ref={searchInputRef}
        search={search}
        onSearchChange={setSearch}
        userDisplayName={userDisplayName}
        userColorSeq={userColorSeq}
        userAvatarUrl={userAvatarUrl}
      />
      <div className={bodyLayoutClass}>
        <MyTasksRail
          sortMode={sortMode}
          onSortModeChange={setSortMode}
          sortCounts={sortCounts}
          compostCounts={counts}
          customers={filterCustomers}
          customerFilter={customerFilter}
          onCustomerFilterChange={setCustomerFilter}
          projectFilter={projectFilter}
          onProjectFilterChange={setProjectFilter}
          availableTiers={availableTiers}
          tierFilter={tierFilter}
          onTierFilterChange={setTierFilter}
          showDone={showDone}
          onShowDoneChange={setShowDone}
          stacked={railStacked}
        />
        <div className="flex-1 flex flex-col overflow-hidden">
          <MyTasksHeader sentence={sentence} subLine={SUB_LINE_BY_MODE[sortMode]} />
          <MyTasksZonedList
            onRowClick={handleOpenTask}
            sortMode={sortMode}
            tasks={sortedTasks}
            zoneGroups={zoneGroups}
            projectGroups={projectGroups}
            density="comfortable"
            filterHint={filterHint}
            onClearFilters={onClearFilters}
            graveyardTrailingSlot={<MtBulkCloseDialog tasks={graveyardCloseTargets} />}
            viewportMode={viewportMode}
            cursorTaskId={cursorTaskId}
            onReady={handleZonedListReady}
            trailing={
              <>
                <MyTasksFreeToGrab onOpen={openFreeToGrab} />
                {grabOpen && <PickSomethingUp />}
              </>
            }
          />
        </div>
      </div>
      <MyTasksTaskPanel taskRef={openTaskRef} onClose={handleCloseTask} />
    </div>
  );
}
